/** Heuristic: href was saved as the image asset URL instead of a page path. */
const ABSOLUTE_IMAGE_HREF_RE =
	/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i;

/**
 * Logo link must be a site path (e.g. `/`), never the image `src` or a media API URL.
 */
export function normalizeMegaMenuLogoHref(
	logoHref: string | undefined,
	logoUrl?: string
): string {
	const href = (logoHref ?? "/").trim() || "/";
	const url = logoUrl?.trim();
	if (url && href === url) {
		return "/";
	}
	if (/\/api\/media\//i.test(href)) {
		return "/";
	}
	if (ABSOLUTE_IMAGE_HREF_RE.test(href)) {
		return "/";
	}
	return href;
}

/** Default logo display height (px) when unset. */
export const MEGA_MENU_LOGO_HEIGHT_DEFAULT_PX = 36;
export const MEGA_MENU_LOGO_HEIGHT_MIN_PX = 16;
export const MEGA_MENU_LOGO_HEIGHT_MAX_PX = 120;

/** Display preset — sets header height and caps delivered raster width. */
export type MegaMenuLogoSizeVariant = "compact" | "default" | "large";

export const MEGA_MENU_LOGO_SIZE_PRESETS: Record<
	MegaMenuLogoSizeVariant,
	{ heightPx: number; rasterMaxPx: number }
> = {
	compact: { heightPx: 28, rasterMaxPx: 280 },
	default: { heightPx: MEGA_MENU_LOGO_HEIGHT_DEFAULT_PX, rasterMaxPx: 400 },
	large: { heightPx: 48, rasterMaxPx: 560 },
};

/** File Manager custom variant keys generated for mega-menu logo delivery. */
export const MEGA_MENU_LOGO_DELIVERY_VARIANT_KEYS: Record<
	MegaMenuLogoSizeVariant,
	string
> = {
	compact: "logo_compact",
	default: "logo_default",
	large: "logo_large",
};

/** Target raster width when generating a logo size variant in File Manager. */
export function resolveMegaMenuLogoVariantTargetWidthPx(
	logoSizeVariant: MegaMenuLogoSizeVariant
): number {
	return MEGA_MENU_LOGO_SIZE_PRESETS[logoSizeVariant].rasterMaxPx;
}

/** Named variant key for publish/runtime logo delivery (File Manager custom key). */
export function resolveMegaMenuLogoDeliveryVariantKey(
	logoSizeVariant: MegaMenuLogoSizeVariant | undefined
): string {
	const variant = logoSizeVariant ?? "default";
	return MEGA_MENU_LOGO_DELIVERY_VARIANT_KEYS[variant];
}

/**
 * Effective size preset for delivery when payload omits `logoSizeVariant`.
 * Custom `logoHeightPx` skips named variants so width-cap fallback applies.
 */
export function resolveMegaMenuLogoDeliverySizeVariant(payload: {
	logoSizeVariant?: unknown;
	logoHeightPx?: unknown;
}): MegaMenuLogoSizeVariant | undefined {
	if (payload.logoSizeVariant != null) {
		return normalizeMegaMenuLogoSizeVariant(payload.logoSizeVariant);
	}
	if (
		typeof payload.logoHeightPx === "number" &&
		Number.isFinite(payload.logoHeightPx)
	) {
		return undefined;
	}
	return "default";
}

/** Branding logo shown while any top nav item is hovered. */
export type MegaMenuLogoOpenVariant = "primary" | "secondary";

export function normalizeMegaMenuLogoOpenVariant(
	value: unknown
): MegaMenuLogoOpenVariant {
	return value === "secondary" ? "secondary" : "primary";
}

export function normalizeMegaMenuLogoSizeVariant(
	value: unknown
): MegaMenuLogoSizeVariant {
	if (value === "compact" || value === "large") {
		return value;
	}
	return "default";
}

/**
 * Effective display height: explicit `logoHeightPx` wins, then size preset, then
 * undefined (runtime CSS default 36px).
 */
export function resolveMegaMenuLogoDisplayHeightPx(
	logoHeightPx: number | undefined,
	logoSizeVariant: MegaMenuLogoSizeVariant | undefined
): number | undefined {
	const explicit = normalizeMegaMenuLogoHeightPx(logoHeightPx);
	if (explicit != null) {
		return explicit;
	}
	if (logoSizeVariant == null || logoSizeVariant === "default") {
		return undefined;
	}
	return MEGA_MENU_LOGO_SIZE_PRESETS[logoSizeVariant].heightPx;
}

/** Max raster width for logo delivery (2× target display width). */
export function resolveMegaMenuLogoRasterMaxPx(
	displayHeightPx: number | undefined,
	logoSizeVariant: MegaMenuLogoSizeVariant | undefined
): number {
	if (logoSizeVariant != null) {
		return MEGA_MENU_LOGO_SIZE_PRESETS[logoSizeVariant].rasterMaxPx;
	}
	const h = displayHeightPx ?? MEGA_MENU_LOGO_HEIGHT_DEFAULT_PX;
	return Math.min(800, Math.max(200, Math.round(h * 10)));
}

/**
 * URL for the open-state logo when variant is secondary and a secondary branding
 * logo exists. Otherwise undefined (keep primary).
 */
export function resolveMegaMenuLogoOpenUrl(
	variant: MegaMenuLogoOpenVariant | undefined,
	secondaryUrl: string | null | undefined
): string | undefined {
	if (variant !== "secondary") {
		return undefined;
	}
	const url = secondaryUrl?.trim();
	return url && url.length > 0 ? url : undefined;
}

/** Clamp a user-entered logo display height (px); returns undefined when unset/invalid. */
export function normalizeMegaMenuLogoHeightPx(
	value: number | undefined
): number | undefined {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return undefined;
	}
	const rounded = Math.round(value);
	if (rounded <= 0) {
		return undefined;
	}
	return Math.min(
		MEGA_MENU_LOGO_HEIGHT_MAX_PX,
		Math.max(MEGA_MENU_LOGO_HEIGHT_MIN_PX, rounded)
	);
}

/** Logo fields restored from `__megaMenuPayloadJson` into builder elements. */
export type MegaMenuLogoElementFields = {
	logoMediaId: string;
	logoAlt: string;
	logoHref: string;
	logoUrl?: string;
	logoHeightPx?: number;
	logoSizeVariant?: MegaMenuLogoSizeVariant;
	logoOpenVariant?: MegaMenuLogoOpenVariant;
};

/**
 * Parse logo fields from a mega-menu runtime payload (builder deserialize).
 * Must stay in sync with `buildMegaMenuRuntimePayload` / audience / advanced builders.
 */
export function readMegaMenuLogoFieldsFromPayload(
	parsedPayload: Record<string, unknown>
): MegaMenuLogoElementFields {
	const logoUrl =
		typeof parsedPayload.logoUrl === "string"
			? parsedPayload.logoUrl
			: undefined;
	const rawHref =
		typeof parsedPayload.logoHref === "string" ? parsedPayload.logoHref : "/";
	const logoHref = normalizeMegaMenuLogoHref(rawHref, logoUrl);
	const logoHeightPx = normalizeMegaMenuLogoHeightPx(
		typeof parsedPayload.logoHeightPx === "number"
			? parsedPayload.logoHeightPx
			: undefined
	);
	const logoOpenVariant = normalizeMegaMenuLogoOpenVariant(
		parsedPayload.logoOpenVariant
	);
	const logoSizeVariantRaw = parsedPayload.logoSizeVariant;
	const logoSizeVariant =
		logoSizeVariantRaw === "compact" ||
		logoSizeVariantRaw === "large" ||
		logoSizeVariantRaw === "default"
			? logoSizeVariantRaw
			: undefined;

	return {
		logoMediaId:
			typeof parsedPayload.logoMediaId === "string"
				? parsedPayload.logoMediaId
				: "",
		logoAlt:
			typeof parsedPayload.logoAlt === "string"
				? parsedPayload.logoAlt
				: "Logo",
		logoHref,
		...(logoUrl == null ? {} : { logoUrl }),
		...(logoHeightPx == null ? {} : { logoHeightPx }),
		...(logoSizeVariant == null || logoSizeVariant === "default"
			? {}
			: { logoSizeVariant }),
		...(logoOpenVariant === "primary" ? {} : { logoOpenVariant }),
	};
}

/** Prefer library-resolved URL, then persisted `logoUrl`. */
export function pickMegaMenuLogoSrc(
	logoUrl: string | undefined,
	resolvedLibraryUrl: string | null | undefined
): string | undefined {
	const resolved = resolvedLibraryUrl?.trim();
	if (resolved) {
		return resolved;
	}
	const direct = logoUrl?.trim();
	return direct || undefined;
}
